import './Baseline.css';

import {useEffect, useState} from 'react';

import {apiService} from '../../../apiService';
import {useAuth} from '../../../contexts/AuthContext';
import {useUserInfo} from '../../../hooks/useUserInfo';
import {BaselineAdminView} from './BaselineAdminView/BaselineAdminView';
import {BaselineManagerView} from './BaselineManagerView/BaselineManagerView';

const Baseline = () => {
  const {currentUser} = useAuth();
  const {
    data: userInfo,
    isMultiOrg,
    isLoading: userInfoLoading,
    error: userInfoError,
  } = useUserInfo();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [tempFilterStatus, setTempFilterStatus] = useState('all');
  const [sortBy, setSortBy] = useState('alphabetical');
  const [showFilterDropdown, setShowFilterDropdown] = useState(false);
  const [showSortDropdown, setShowSortDropdown] = useState(false);
  const [collapsedLocations, setCollapsedLocations] = useState({});

  useEffect(() => {
    if (!userInfoLoading && !userInfoError && currentUser?.uid) {
      fetchBaselineCompliance();
    }
  }, [userInfoLoading, userInfoError, isMultiOrg, currentUser?.uid]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.baseline-dropdown-container')) {
        setShowFilterDropdown(false);
        setShowSortDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const fetchBaselineCompliance = async () => {
    if (!currentUser?.uid) return;

    try {
      setLoading(true);
      let url = `/api/get-manager-baseline-compliance?uid=${currentUser.uid}`;

      const data = await apiService.get(url);

      if (data) {
        setData(data.body.results);
      } else {
        setError(data.error || 'Failed to fetch baseline compliance data');
      }
    } catch (err) {
      setError('Network error occurred');
    } finally {
      setLoading(false);
    }
  };

  const toggleLocationCollapse = (locationName) => {
    setCollapsedLocations((prev) => ({
      ...prev,
      [locationName]: !prev[locationName],
    }));
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'completed':
        return 'completed';
      case 'in_progress':
        return 'in progress';
      case 'not_started':
        return "hasn't started";
      default:
        return 'unknown';
    }
  };

  if (userInfoError) {
    return (
      <div className='baseline-error'>
        <div className='baseline-error-title'>Error</div>
        <div className='baseline-error-message'>
          Failed to determine user permissions
        </div>
      </div>
    );
  }

  if (loading || userInfoLoading) {
    return (
      <div className='baseline-loading'>
        <div className='baseline-loading-spinner'></div>
        <span className='baseline-loading-text'>
          Loading baseline compliance data...
        </span>
      </div>
    );
  }

  if (error) {
    return (
      <div className='baseline-error'>
        <div className='baseline-error-title'>Error</div>
        <div className='baseline-error-message'>{error}</div>
        <button
          onClick={fetchBaselineCompliance}
          className='baseline-error-retry'
        >
          Try Again
        </button>
      </div>
    );
  }

  if (isMultiOrg) {
    return (
      <BaselineAdminView
        data={data}
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        sortBy={sortBy}
        setSortBy={setSortBy}
        showSortDropdown={showSortDropdown}
        setShowSortDropdown={setShowSortDropdown}
        collapsedLocations={collapsedLocations}
        toggleLocationCollapse={toggleLocationCollapse}
        getStatusLabel={getStatusLabel}
      />
    );
  }

  return (
    <BaselineManagerView
      data={data[0]?.employees || []}
      orgId={userInfo?.orgId}
      searchTerm={searchTerm}
      setSearchTerm={setSearchTerm}
      sortBy={sortBy}
      setSortBy={setSortBy}
      filterStatus={filterStatus}
      setFilterStatus={setFilterStatus}
      tempFilterStatus={tempFilterStatus}
      setTempFilterStatus={setTempFilterStatus}
      showSortDropdown={showSortDropdown}
      setShowSortDropdown={setShowSortDropdown}
      showFilterDropdown={showFilterDropdown}
      setShowFilterDropdown={setShowFilterDropdown}
      getStatusLabel={getStatusLabel}
    />
  );
};

export default Baseline;
